'use client';

import { BOT_SHEET, readLayoutMode, useIsApp, webOnly } from './layout-mode';

// Страница сценария: переключатель «Диалог | Схема» над рабочей зоной.
// Диалог — чат с ботом (этап 3: правит канон черновика инструментами scenario_*);
// «Схема» — два вида одного канона (n8n-JSON): холст с зумом/паном и вертикальная
// лента для телефона (VERTICAL-SCHEMA-PLAN этап 2, переключатель внутри панели).
// Старый ленточный редактор на graphToN8n снесён (терял fan-out; VERTICAL-SCHEMA
// этап 5). «Проверить» (этап 6) — детерминированный путь без
// бота: установка → пробный запуск → проверен и включён; упавший тест чинит ассистент.
// Готовый (deployed/verified) открывается конструктором, та же кнопка «Проверить»
// (одно имя действия для черновика и готового, Ф29) делает редеплой + смоук A5 (этап B6).
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { useTranslations } from 'next-intl';
import { PopSelect, type ScenarioGraph } from '@scriptera/ui';
import { botAccountState, emptyWorkflow, type WorkflowJson } from '@scriptera/ui/scenario';
import { chatBotBusy } from '@/lib/chat-pending';
import { forgetPersistent, usePersistentData } from '@/lib/use-persistent-data';
import { useFavorites } from '@/lib/use-favorites';
import { catList } from '@/lib/script-cats';
import { Link, useRouter } from '@/lib/cabinet-nav';
import { topIsPanel, useSlidePanel } from './native-layer-host';
// К35 (владелец 2026-07-31): все окна страницы сценария — одна вертикальная шторка снизу,
// открытая не до верха (сверху видна полоса схемы), одинаково везде.
import { EDITOR_SHEET } from '@/lib/sheet-detent';
import { DError, DGroup } from './drawer-kit';
import { FBlock, FChoiceBlock, FPane } from './form-kit';
import { KBareButton, KButton, KInput, KTextarea, KToast } from '@scriptera/ui/kit';
import { ScenarioChat, type DraftSnapshot } from './chat';
import { TemplateWizardPanel } from './template-wizard';
import { useWizEditSheet } from './wizard-host';
import { CHAIN_WIZARD_ID, wizardById, wizardEditAbout } from '@/lib/template-wizards';
import { useScriptRows } from './split-menu';
import { ChainPagePane } from './chain-page';
import { ScenarioSharePanel } from './scenario-share';
import { DOTS } from './card-tools';
import {
  ScnMenuList,
  ViewCanvasIcon,
  ViewDialogIcon,
  ViewLanesIcon,
  scenarioMenuItems,
  scenarioMenuNativeItems,
} from './scenario-menu';
import { useScenarioRun } from './use-scenario-run';
import { ASSISTANT_HIDDEN, BUILDER_HIDDEN } from '@/lib/hidden-features';
import { hasNativeLayers, nativeLayerOpen, popLayer, subscribeTopLayer } from '@/lib/native-layers';
import { nativeBridge, nativeMethod } from '@/lib/native-app';
import { reportUiError } from '@/lib/ui-error-report';

// Холст тянет @xyflow/react — грузим лениво при первом открытии вкладки «Схема».
const ScenarioSchemaPanel = dynamic(
  () => import('./scenario-schema-panel').then((m) => m.ScenarioSchemaPanel),
  {
    ssr: false,
    // ТИХАЯ заглушка (владелец 2026-08-03: «придурочный скелетон… потом с дёрганьем
    // заменяется»): просто пустой холст цвета фона — карточки появляются на нём без
    // промежуточной мельтешни; чанк и так греется при открытии страницы.
    loading: () => <div className="scn-schema-wrap canvas scn-canvas-wait" aria-hidden />,
  },
);

interface DraftData {
  ok: boolean;
  draft: DraftSnapshot;
  /** Цепочка: сколько людей в ней идут или ждут (MSG-VERIFY-FIX В); у остальных ботов поля нет. */
  chainRunning?: number;
}

/** Режим схемы бота при открытии (В2, владелец 18.08): выбор этой вкладки важнее статуса —
 *  без выбора запущенный бот открывается ПРОСМОТРОМ (его схему чаще читают, чем правят),
 *  неразвёрнутый черновик — редактированием. Считается ПРИ РЕНДЕРЕ (Р12), а не в эффекте:
 *  пока данных нет, схему всё равно не рисуют (страница держит шапку и скелетон), а первый
 *  кадр с данными обязан прийти уже в нужном режиме — иначе он моргает приглашениями. */
function initialViewOnly(draftId: string, data: DraftData | null): boolean {
  if (!data || typeof window === 'undefined') return false;
  const saved = window.sessionStorage.getItem(`scn-view-only:${draftId}`);
  return saved !== null ? saved === '1' : data.draft.status !== 'draft';
}

/* Вид схемы (DESIGN-REFRESH этап 2): состояние живёт здесь — переключалка в плавающей
   шапке, панель схемы только рисует выбранный вид. Дефолт СТРОГО по ширине экрана
   (десктоп → холст, узкий → лента; заказ владельца 2026-07-10) — выбор живёт только
   в рамках открытой страницы, в localStorage НЕ запоминается: раньше сохранённая
   «лента» липла и на десктопе. */
export type SchemaView = 'canvas' | 'lanes';

function initialView(): SchemaView {
  return window.matchMedia('(max-width: 719px)').matches ? 'lanes' : 'canvas';
}

// Граф из jsonb — недоверенная форма: рендерим только похожее на ScenarioGraph.
function asGraph(raw: unknown): ScenarioGraph | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const g = raw as { steps?: unknown };
  return Array.isArray(g.steps) ? (raw as ScenarioGraph) : null;
}

export function ScenarioWorkspace({
  draftId: propDraftId,
  initialMode,
}: {
  draftId: string;
  /** Стартовая вкладка (B4): пути «Собрать схему»/«Импорт JSON» открывают сразу холст. */
  // 'wizard' — только у мастера СО СХЕМОЙ (рассылка): у остальных анкет он и так
  // единственный вид, называть его адресом не нужно.
  initialMode?: 'dialog' | 'schema' | 'wizard';
}) {
  const t = useTranslations('cabinet.scenario');
  const tc = useTranslations('cabinet'); // Да/Нет для подтверждений
  const router = useRouter();

  // ── Отложенный черновик (владелец 2026-07-20) ── «Собрать схему»/«Собрать через чат»
  // открывают ПУСТОЙ редактор с id='new' и НЕ создают черновик (иначе плодятся пустышки).
  // Черновик рождается при ПЕРВОМ действии (сообщение в чате / узел на холсте): ensureDraftId
  // делает POST /drafts, подменяет id и URL через replaceState — страница НЕ перемонтируется
  // (стейт чата/холста и нативный слой целы), history.back по-прежнему ведёт к списку.
  const [draftId, setDraftId] = useState(propDraftId);
  const draftless = draftId === 'new';
  const creatingRef = useRef<Promise<string> | null>(null);
  const ensureDraftId = useCallback((): Promise<string> => {
    if (draftId !== 'new') return Promise.resolve(draftId);
    if (creatingRef.current) return creatingRef.current;
    const p = fetch('/api/cabinet/scenarios/drafts', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({}),
    })
      .then((r) => r.json())
      .then((j: { ok?: boolean; id?: string }) => {
        if (!j?.ok || !j.id) throw new Error('create_failed');
        setDraftId(j.id);
        // Адрес сценария — /app/bots/<id> (2026-08-03); старый /automations/new жив
        // на миг до редиректа — меняем оба написания.
        const nu = window.location.pathname.replace(/\/(automations|scripts|bots)\/new(?=\/|$)/, `/bots/${j.id}`);
        window.history.replaceState(null, '', nu + window.location.search);
        return j.id;
      });
    creatingRef.current = p;
    p.catch(() => {
      creatingRef.current = null; // дать повторить, если создание сорвалось
    });
    return p;
  }, [draftId]);

  // Закрытие крестиком — сразу навигация к списку. Веб-анимации ухода нет (ТЗ WEB-LOOK Э5):
  // в браузере бот — панель над списком, а в приложении слой снимает натив.
  // ЕДИНЫЙ выход из сценария. Приложение: если сценарий открыт боковым слоем — снять слой
  // родным выездом назад (натив анимирует, веб делает шаг назад в history) и вернуть true.
  // ЛЮБОЙ выход к списку (крестик, удаление, ошибка) обязан идти через него: иначе слой
  // остаётся, снимок висит — свайпом возвращаешься к закрытому/удалённому сценарию, и так
  // рекурсивно (замечание владельца 2026-07-20). false — слоя нет, вызывающий навигирует сам.
  const exitScenarioLayer = (): boolean => {
    if (hasNativeLayers() && nativeLayerOpen()) {
      void popLayer();
      return true;
    }
    return false;
  };
  const closeToList = (e: React.MouseEvent) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    if (exitScenarioLayer()) return;
    router.push('/app/bots');
  };

  const {
    data: cachedData,
    error,
    errorStatus,
    mutate,
    reload,
  } = usePersistentData<DraftData>(
    `cabinet:scenario-draft:${draftId}`,
    // Режим 'new' — не грузим (черновика ещё нет): пустой url = пропуск запроса.
    draftless ? '' : `/api/cabinet/scenarios/drafts/${draftId}`,
    { accept: (j) => !!(j as { ok?: boolean })?.ok },
  );
  // INVARIANT(scriptera): у ПУСТОГО редактора ('new') кэш ключа не читаем. Ключ временный и
  // общий на все новые чаты: стоило туда однажды осесть снимку разговора — и каждый новый
  // чат открывался чужой перепиской, переживая перезагрузку (проба владельца 2026-08-01).
  const data = draftless ? null : cachedData;
  // ...и подчищаем запись, если она уже осела на устройстве до этой правки.
  useEffect(() => {
    if (draftless) forgetPersistent('cabinet:scenario-draft:new');
  }, [draftless]);
  // INVARIANT(scriptera): о СУЩЕСТВОВАНИИ бота истина только у сервера — 404 перевешивает
  // снимок на устройстве. Причина: кэш переживает удаление (в т.ч. с этой же страницы), и без
  // этого возврат на адрес удалённого бота открывал полностью рабочий редактор-призрак: схема
  // рисовалась из кэша, а каждая правка уходила в PUT и получала 404 — работа человека
  // терялась молча, а центр ошибок ловил ложный алярм (инцидент e50ad1b9, «шаги переставлены:
  // not_found»). Флаг залипающий: forgetPersistent сбрасывает и снимок, и признак ошибки.
  const [gone, setGone] = useState(false);
  const markGone = useCallback(() => {
    setGone(true);
    forgetPersistent(`cabinet:scenario-draft:${draftId}`);
  }, [draftId]);
  useEffect(() => {
    if (!draftless && errorStatus === 404) markGone();
  }, [draftless, errorStatus, markGone]);
  // null = вкладку никто не выбирал: черновик откроется «Диалогом», готовый
  // (deployed/verified) — конструктором «Схема» (B6), а рождённый мастером —
  // МАСТЕРОМ, не схемой (владелец 2026-08-05: «создал через виджет — открывает
  // виджет»); статус известен после загрузки.
  const [chosenMode, setMode] = useState<'dialog' | 'schema' | 'wizard' | null>(initialMode ?? null);
  // Анкета-мастер черновика (FORM-WIZARD Ф1) — режим «Настройка» существует только у
  // сценария, рождённого мастером, и он же его режим по умолчанию.
  const wizDef = data?.draft.wizard ? wizardById(data.draft.wizard.id) : undefined;
  // ЖЕЛЕЗНОЕ (владелец 05.08): скрипт из быстрой настройки редактируется ТОЛЬКО ею —
  // холста/ленты/диалога у него нет вовсе, переключалка видов не рисуется. Иначе ручные
  // правки схемы молча стирались бы пересборкой анкеты (wizEditNote предупреждал, но
  // владелец закрыл сам путь).
  // Исключений больше НЕТ: прежнее «мастер со схемой» (рассылка) снято владельцем
  // 31.08.2026 — рассылку конструктором не открывают. Любой бот, рождённый анкетой,
  // правится только этой анкетой.
  const wizardOnly = !!wizDef;
  // Текущий режим (дублирует расчёт mode ниже — тот живёт после раннего return, а хукам
  // положено стоять до него): схема после первого показа остаётся смонтированной
  // (keep-mounted, владелец 2026-08-03 «кэшить схемы») — флаг schemaSeen.
  // ASSISTANT_HIDDEN (владелец 29.08): «Диалога» не бывает НИ У КОГО — ни у черновика по
  // умолчанию, ни выбором вида, ни залётным ?view=dialog; всё сводится к схеме.
  const modeAsked: 'dialog' | 'schema' | 'wizard' = wizardOnly
    ? 'wizard'
    : (chosenMode ?? (draftless && !ASSISTANT_HIDDEN ? 'dialog' : 'schema'));
  const modeNow: 'dialog' | 'schema' | 'wizard' =
    ASSISTANT_HIDDEN && modeAsked === 'dialog' ? 'schema' : modeAsked;
  // ШИРОКИЙ ЭКРАН: мастерский бот открывается ТОЙ ЖЕ ПРАВОЙ ПАНЕЛЬЮ, что создание из «+»
  // (useWizEditSheet; паритет веб = приложение, владелец 09.09.2026) — фулскрин-страницы
  // сценария для него на десктопе нет, а прежнее правое поле «быстрой настройки»
  // (/app/bots/quick/<id>, 05.08) снято. Прямой адрес /app/bots/<id> ведёт в список ботов
  // и поднимает шторку поверх него.
  const openWizSheet = useWizEditSheet();
  const wizSheetOnce = useRef(false);
  // БРАУЗЕР (ТЗ WEB-LOOK Э2.1): страницы бота нет — мастер открывается панелью над списком
  // «Ботов» на любой ширине, адрес остаётся /app/bots/<id> (его держит сама панель, href).
  // В приложении — как прежде (запасной путь старых сборок).
  const isApp = useIsApp();
  // ЦЕПОЧКА — ИСКЛЮЧЕНИЕ: по этому адресу у неё живёт СВОЯ страница (М1.5/М1.6), и
  // уводить с неё в список нельзя. Правку она открывает мастером поверх себя.
  // Признак цепочки — строка bot_chains (флаг chainActive в списке ботов), а не только id
  // анкеты: у цепочек, заведённых до 16.09, запись анкеты хранит `slug`, и wizDef пуст.
  const scriptRows = useScriptRows();
  const isChain =
    wizDef?.id === CHAIN_WIZARD_ID || scriptRows.some((r) => r.draftId === draftId && r.chain === true);
  // Адрес /app/bots/<id> у цепочки в вебе попадает под фокус-режим оболочки (нет шапки
  // раздела и нижнего меню) — штатный адрес её страницы /app/bots/<id>/stats, туда и
  // переводим; страница одна и та же (ChainPagePane), мелькания нет.
  useEffect(() => {
    if (isChain) router.replace(`/app/bots/${draftId}/stats`);
  }, [isChain, draftId, router]);
  useEffect(() => {
    if (!wizardOnly || isChain || draftless || wizSheetOnce.current) return;
    if (isApp && readLayoutMode() !== 'three') return;
    wizSheetOnce.current = true;
    // Приложение: прежний ход — адрес списка + шторка. Браузер: адрес не трогаем, панель
    // с href сама ведёт историю (Э0.4).
    if (isApp) router.replace('/app/bots');
    // Отдельной задачей планировщика: drawer.open зовёт flushSync, а эффект может бежать
    // внутри синхронного дорендера после гидрации.
    window.setTimeout(() => openWizSheet(draftId, () => {}), 0);
  }, [wizardOnly, isChain, draftless, draftId, router, openWizSheet, isApp]);
  // БОТ БЕЗ АНКЕТЫ ОТКРЫВАТЬ НЕЧЕМ, пока спрятан конструктор (владелец 31.08.2026), и в
  // списке его тоже больше нет — прямой адрес уводим в список ботов. Сюда же попадают
  // старые ссылки с ?view=canvas|lanes.
  useEffect(() => {
    if (!BUILDER_HIDDEN || wizDef) return;
    // Бот ещё грузится (data нет, анкета не известна) — решать рано; ПУСТОЙ ХОЛСТ
    // (/app/bots/new, draftless) под скрытым конструктором не открывается вовсе
    // (проверка владельца 02.09: «конструктор не должен видеть пользователю никогда»).
    if (!draftless && !data) return;
    router.replace('/app/bots');
  }, [data, wizDef, draftId, draftless, router]);
  const [schemaSeen, setSchemaSeen] = useState(false);
  useEffect(() => {
    if (modeNow === 'schema') setSchemaSeen(true);
  }, [modeNow]);
  // Чанк холста (@xyflow) греется СРАЗУ при открытии страницы, не при первом щелчке
  // по вкладке (владелец 2026-08-03: «открывался быстро и сразу как надо»).
  useEffect(() => {
    void import('./scenario-schema-panel');
  }, []);
  // Вид схемы: null до маунта (localStorage/ширина читаются на клиенте — иначе
  // разъезд SSR/гидрации на сегменте в шапке), первый кадр ставит реальный вид.
  const [schemaView, setSchemaView] = useState<SchemaView | null>(null);
  // useLayoutEffect, а не useEffect: реальный вид ставится ДО первой отрисовки, поэтому
  // null-кадр (шапка в переходном виде) на экран не попадает — кнопки не «подскакивают»
  // после открытия сценария (владелец 2026-07-20). SSR по-прежнему рисует null (гидрация
  // без разъезда), а на клиенте вид проставляется до paint.
  useLayoutEffect(() => {
    setSchemaView(initialView());
  }, []);
  const pickView = (v: SchemaView) => setSchemaView(v);
  // ── В2 (владелец 18.08): режим схемы — «Редактирование» ⇄ «Просмотр» ──
  // В просмотре схема лаконичная: пунктирные приглашения «добавить…» спрятаны и
  // возвращаются только у карточки, по которой тапнули (В3/В4, CSS по классу
  // .scn-view-only). Дефолт: неразвёрнутый черновик открывается в редактировании,
  // уже запущенный бот — в просмотре (его схему чаще читают, чем правят). Выбор
  // держится на время вкладки (sessionStorage по боту), между сессиями не липнет.
  // Р12 (ревью 18.08): выбор ПРИВЯЗАН К БОТУ и дефолт считается ПРИ РЕНДЕРЕ.
  // Привязка к id — потому что раздел живёт одним catch-all роутом: переход
  // /app/bots/A → /app/bots/B компонент не перемонтирует, и прежний признак «режим уже
  // выбран» записывал режим бота A в ключ бота B. Расчёт при рендере — потому что
  // setState в эффекте давал лишний кадр в редактировании: у запущенного бота схема
  // моргала пунктирными приглашениями и только потом становилась лаконичной.
  const [modePick, setModePick] = useState<{ id: string; on: boolean } | null>(null);
  const viewOnly = modePick && modePick.id === draftId ? modePick.on : initialViewOnly(draftId, data);
  useEffect(() => {
    // Дефолт ПРИШПИЛИВАЕМ: дальше режим держит выбор человека, а не статус бота — иначе
    // развернувшийся посреди сессии черновик сам перескочил бы в просмотр.
    if (!data || modePick?.id === draftId) return;
    setModePick({ id: draftId, on: initialViewOnly(draftId, data) });
  }, [data, draftId, modePick]);
  useEffect(() => {
    if (!modePick || modePick.id !== draftId) return;
    window.sessionStorage.setItem(`scn-view-only:${draftId}`, modePick.on ? '1' : '0');
  }, [modePick, draftId]);
  const toggleViewOnly = () => setModePick({ id: draftId, on: !viewOnly });
  // С3 (владелец 18.08): «сохраняю…/сохранено» стоит ВТОРОЙ СТРОКОЙ ШАПКИ, под названием
  // вида, — прежний плавающий чип посреди схемы убран («вываливается где попало»).
  // Техбеду сюда не пишем (запрет №7): не сохранилось — уходит дежурному из
  // use-draft-editor (reportUiError), человек видит только мимолётные «сохраняю…/сохранено».
  const [saveStatus, setSaveStatus] = useState<string | null>(null);
  // Пробный запуск с живой подсветкой шагов (разрез start/complete + опрос) — общий хук;
  // конфликт A12 («один бот = один активный сценарий») тоже приходит из него.
  const run = useScenarioRun({
    draftId,
    draft: data?.draft ?? null,
    onDraft: (d) => mutate({ ok: true, draft: d }),
  });
  // Удаление черновика (B4): двойное подтверждение; пока бот работает — нельзя
  // (клиентский дизейбл — бест-эффорт, истина у DELETE-роута: 409 bot_busy).
  const [chatBusy, setChatBusy] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // Тост «Сохранено» после сохранения мастером (режим «Настройка» остаётся открытым).
  const [wizSaved, setWizSaved] = useState(false);
  // ── 3.21 (владелец 18.08.2026): «⋯» ВЕРНУЛАСЬ В ШАПКУ, отменяя В1 того же дня
  // («это была плохая идея»). Содержимое — тот же общий набор, что у карточки списка и
  // быстрой панели (scenarioMenuItems): О сценарии · Поделиться · Запуски · Удалить.
  // Переключатель «Просмотр ⇄ Редактирование» из шапки НЕ выброшен — он уехал вниз,
  // к кнопке «+» рабочей панели (scenario-schema-panel).
  const [menuOpen, setMenuOpen] = useState(false);
  const [delConfirm, setDelConfirm] = useState(false);
  // Клик по пустому месту закрывает меню. Не fixed-бэкдропом: backdrop-filter стеклянной
  // шапки (G5) делает её containing block для fixed — бэкдроп сжимался до полосы шапки и
  // клики ниже неё не ловил. Слушаем pointerdown на документе (кнопка «⋯» — внутри обёртки).
  const menuWrapRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: PointerEvent) => {
      const w = menuWrapRef.current;
      if (w && e.target instanceof Node && !w.contains(e.target)) {
        setMenuOpen(false);
        setDelConfirm(false);
      }
    };
    document.addEventListener('pointerdown', onDown, true);
    return () => document.removeEventListener('pointerdown', onDown, true);
  }, [menuOpen]);
  // Кнопка-телефон превью мастера — В ШАПКЕ СТРАНИЦЫ, как при создании (владелец
  // 2026-08-06: «открываю снова — должно быть один в один как при создании»). Сам
  // обработчик приносит панель мастера через проп header; она же из-за этого пропа
  // перестаёт рисовать свою строку со счётчиком шагов — счётчик уже есть в нижней планке.
  const wizPreviewRef = useRef<() => void>(() => {});
  // Стоит ли кнопка-телефон в родной шапке страницы: на шаге «Что запускает» её нет
  // вовсе (К19.1 п.9, владелец 06.09.2026) — мастер шлёт `setPreview(null)`.
  const [wizPhone, setWizPhone] = useState(true);
  // Drawer «О сценарии» (этап 4): название + описание; ✓ шапки дергает save формы.
  // useSlidePanel: в приложении — нативный слой (about — нижний лист), в вебе — прежний drawer.
  const drawer = useSlidePanel();
  const aboutSaveRef = useRef<(() => void) | null>(null);
  // Чанк холста (@xyflow) греется СРАЗУ при открытии страницы, не при первом щелчке по
  // вкладке (владелец 2026-08-03: «открывался быстро и сразу как надо»).
  useEffect(() => {
    void import('./scenario-schema-panel');
  }, []);

  const removeDraft = async () => {
    // Пустой редактор ('new') — черновика на сервере ещё нет, удалять нечего: просто закрыть.
    if (draftless) {
      if (!exitScenarioLayer()) router.push('/app/bots');
      return;
    }
    if (deleting) return;
    setDeleting(true);
    run.setNote(null);
    const r = await fetch(`/api/cabinet/scenarios/drafts/${draftId}`, { method: 'DELETE' })
      .then((res) => res.json())
      // network: обрыв сети/подмена контейнеров при выкатке — до сервера не долетело
      .catch(() => ({ ok: false, error: 'network' }));
    if (r.ok) {
      // Снимок удалённого бота на устройстве держать нельзя: возврат на его адрес открыл бы
      // редактор-призрак из кэша (см. инвариант о 404 выше).
      forgetPersistent(`cabinet:scenario-draft:${draftId}`);
      // Удаление тоже снимает слой родным выездом (иначе снимок висит — свайпом вернёшься
      // к удалённому); слоя нет — обычная навигация к списку.
      if (!exitScenarioLayer()) router.push('/app/bots');
      return; // deleting держим до ухода со страницы
    }
    setDeleting(false);
    // Клиент видит тост — смотрящий обязан видеть то же (владелец 2026-07-19):
    // осмысленный отказ bot_busy не репортим, остальное — в центр ошибок.
    if (r.error !== 'bot_busy') {
      reportUiError('draft_delete_failed', `удаление черновика не прошло: ${r.error ?? 'unknown'}`, {
        draftId,
      });
    }
    run.setNote({ ok: false, text: r.error === 'bot_busy' ? t('deleteBusyNote') : t('deleteError') });
  };

  // Режим 'new' — пустой черновик локально (на сервере ещё нет); первое действие его создаст.
  const emptyDraft: DraftSnapshot = {
    id: 'new',
    title: tc('newScenario'),
    status: 'draft',
    // version РОВНО как у свежесозданного черновика (POST /drafts создаёт version:1) — иначе
    // первый PUT узла ушёл бы с baseVersion 0 и словил 409 против version 1 на сервере.
    version: 1,
    graph: { steps: [] },
    workflow: emptyWorkflow(tc('newScenario')),
    nodeInfo: {},
    creds: [],
    lint: { errors: [] },
    chat: [],
  };
  const draft = data?.draft ?? emptyDraft;
  // Готовый сценарий (B6): открывается конструктором, «Проверить» — тот же
  // детерминированный путь (редеплой + смоук A5).
  const deployed = draft.status !== 'draft';
  // Открытие (владелец 2026-07-24): СУЩЕСТВУЮЩИЙ сценарий — всегда Схема (вид Холст/Лента по
  // устройству, initialView), НИКОГДА не Чат. Чат — только при СОЗДАНИИ с выбором «через чат»
  // (new?view=chat → chosenMode пуст, но draftless=true). Явный выбор (chosenMode) — приоритетно.
  const mode = modeNow;
  const graph = asGraph(draft.graph);
  // Бот в работе: локальный ход чата или СВЕЖИЙ pending-маркер в истории (возобновление).
  // Свежесть обязательна: осиротевший маркер умершего хода (рестарт web) иначе навсегда
  // блокировал удаление сценария (скрин владельца IMG_0062, 2026-07-09).
  const botBusy = chatBusy || chatBotBusy(draft.chat);
  // «О сценарии» и «Поделиться» — ДВЕ отдельные шторки (владелец 2026-07-23; раньше
  // шеринг жил секцией внутри «О сценарии»). Обе — вертикальные листы везде
  // (направление одно на мобильный сайт и приложение, владелец 2026-07-21).
  const openAbout = () => {
    // К35: вертикальный лист, как все окна страницы сценария (была горизонтальная шторка,
    // владелец 2026-07-23 — заказ 31.07 её отменил в пользу единого вида).
    drawer.open({
      title: t('aboutTitle'),
      ...EDITOR_SHEET,
      // Всё про одного бота — стопкой над окном бота (ТЗ WEB-LOOK Э2.5); в приложении как было.
      ...webOnly({ winKey: BOT_SHEET }),
      actionLabel: t('aboutSave'),
      actionIcon: 'checkmark', // справа всегда круглая иконка, не текст (владелец 2026-07-21)
      onAction: () => aboutSaveRef.current?.(),
      body: (
        <ScenarioAboutForm
          draft={draft}
          saveRef={aboutSaveRef}
          onSaved={(d) => mutate({ ok: true, draft: d })}
        />
      ),
    });
  };
  const openShare = () => {
    drawer.open({
      title: t('shareTitle'),
      ...EDITOR_SHEET,
      // Всё про одного бота — стопкой над окном бота (ТЗ WEB-LOOK Э2.5); в приложении как было.
      ...webOnly({ winKey: BOT_SHEET }),
      body: <ScenarioSharePanel draftId={draft.id} title={draft.title} />,
    });
  };
  // «Настроить» (FORM-WIZARD Ф1, ЖЕЛЕЗНОЕ владельца 04.08 «создал через виджет —
  // редактирует через виджет», усилено 05.08): сценарий рождён мастером (wizard на
  // черновике) → мастер — ЕГО РЕЖИМ ПО УМОЛЧАНИЮ (рисуется вместо схемы, wizDef выше);
  // «Сохранить» пересобирает схему новой ревизией.
  // Ф2: у сценария, созданного до появления аккаунта в анкете, ответа accountId нет —
  // подставляем привязанный к узлам аккаунт из канона (chosenId считает то же ядро
  // botAccountState, что конструктор), чтобы правка не требовала выбирать заново.
  const wizardInitial = (): Record<string, unknown> => {
    // «О боте» — нынешние название и описание бота, а не ответ прошлой записи (К3.2).
    let initial = wizardEditAbout(draft.wizard?.answers ?? {}, draft);
    if (wizDef?.accountChannel && !(typeof initial.accountId === 'string' && initial.accountId)) {
      const wf = draft.workflow as WorkflowJson | null | undefined;
      const chosen = wf && Array.isArray(wf.nodes) ? botAccountState(wf, [])?.chosenId : null;
      if (chosen) initial = { ...initial, accountId: chosen };
    }
    return initial;
  };

  // ── Нативная шапка сценария (владелец 2026-07-21, по HIG) ── обработчики событий
  // headerAction из натива: × / тап по заголовку / пункты UIMenu. Всё, что было в веб-шапке,
  // осталось: вид — отмечаемой группой меню, «О сценарии» (название+описание+поделиться) —
  // тапом по заголовку и пунктом меню, Запуски/Проверить/Удалить — пунктами с иконками.
  const closeNow = () => {
    if (!exitScenarioLayer()) router.push('/app/bots');
  };
  // Открывалки «Запусков» тут больше нет: пункт меню удалён (владелец 31.08.2026 —
  // «это раздел аналитики»), журнал живёт на странице цифр бота (bot-stats.tsx).
  const openDeleteConfirm = () => {
    drawer.open({
      title: t(deployed ? 'deleteScenarioBtn' : 'deleteBtn'),
      ...EDITOR_SHEET,
      // Короткое подтверждение — низкий лист в браузере (ТЗ WEB-LOOK Э2.5); в приложении как было.
      ...webOnly({ sheetDetent: 0.4 }),
      body: (
        <DGroup>
          <span className="confirm2-q">{t(deployed ? 'deleteConfirmDeployed' : 'deleteConfirm')}</span>
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <KButton
              tone="danger"
              size="sm"
              disabled={deleting || botBusy}
              onClick={() => {
                drawer.close();
                void removeDraft();
              }}
            >
              {tc('yes')}
            </KButton>
            <KButton size="sm" onClick={() => drawer.close()}>
              {tc('no')}
            </KButton>
          </div>
        </DGroup>
      ),
    });
  };
  const onHeaderAction = (key: string) => {
    if (key === 'wiz:preview') {
      wizPreviewRef.current();
      return;
    }
    // КАПКАН МОСТА (запрет №8): ключи родной шапки принимаем ВСЕ и всегда — и набор «⋯»
    // (about/share/runs/run/delete, вернувшийся с 3.21), и 'mode' от кнопки-действия
    // «глаз/карандаш», которую 3.21 из шапки убрала: сборки в сторе обновляются месяцами
    // и продолжают её слать. Перестанем принимать — у необновившихся кнопка молча умрёт.
    if (key === 'mode') toggleViewOnly();
    else if (key === 'close') closeNow();
    else if (key === 'title' || key === 'about') openAbout();
    else if (key === 'wizard' || key === 'view:wizard') setMode('wizard');
    // wizardOnly: смена вида запрещена (владелец 05.08) — глушим и залётные ключи.
    else if (key.startsWith('view:') && wizardOnly) return;
    else if (key === 'share') openShare();
    else if (key === 'view:dialog') {
      if (!ASSISTANT_HIDDEN) setMode('dialog');
    }
    else if (key === 'view:canvas') {
      setMode('schema');
      pickView('canvas');
    } else if (key === 'view:lanes') {
      setMode('schema');
      pickView('lanes');
    } else if (key === 'run') run.start();
    else if (key === 'delete') openDeleteConfirm();
  };
  // Заголовок шапки = ПЕРЕКЛЮЧАТЕЛЬ ВИДА (владелец 2026-07-21): по центру — текущий вид
  // с его иконкой, тап открывает родное меню видов (галка на текущем); из «⋯» виды убраны.
  // Название сценария живёт в «О сценарии» (пункт «⋯»).
  // «Настройка» (мастер) — полноправный вид у сценария из мастера (владелец 05.08),
  // первым в меню видов; из «⋯» пункт «Настроить» убран — он теперь вид, не действие.
  const VIEW_META = {
    wizard: { label: t('modeWizard'), icon: 'wand.and.stars' },
    dialog: { label: t('modeDialog'), icon: 'bubble.left.and.bubble.right' },
    canvas: { label: t('vertViewCanvas'), icon: 'point.topleft.down.curvedto.point.bottomright.up' },
    lanes: { label: t('vertViewLanes'), icon: 'list.bullet' },
  } as const;
  const curView: keyof typeof VIEW_META =
    mode === 'wizard' ? 'wizard' : mode === 'dialog' ? 'dialog' : schemaView === 'canvas' ? 'canvas' : 'lanes';
  // Скрипт из быстрой настройки (wizardOnly): видов НЕТ вообще (владелец 05.08 —
  // «создал быстрой настройкой — только там и редактируешь, других путей нет»),
  // меню видов не рисуем ни в вебе, ни в нативной шапке.
  const titleItems = wizardOnly
    ? []
    : (Object.keys(VIEW_META) as Array<keyof typeof VIEW_META>)
        .filter((k) => (k !== 'wizard' || !!wizDef) && (k !== 'dialog' || !ASSISTANT_HIDDEN))
        .map((k) => ({
          key: `view:${k}`,
          label: VIEW_META[k].label,
          icon: VIEW_META[k].icon,
          checked: curView === k,
        }));
  // 3.21 (владелец 18.08.2026): В РОДНОЙ ШАПКЕ ТОЖЕ «⋯» С ПОЛНЫМ НАБОРОМ — во всех видах,
  // а не только вне схемы (Р11 чинил этим пустую кнопку в «Диалоге»; теперь правило одно).
  // Кнопки-действия «глаз/карандаш» в шапке больше нет: переключатель режима уехал вниз,
  // к «+» рабочей панели. В приложении нижний док рисует НАТИВ (configureDock), а его
  // набор кнопок заморожен вместе с мостом (запрет №8: новое поведение — только новым
  // методом), поэтому там переключатель остаётся ПУНКТОМ этого же меню — «Режим: …».
  // Веб (и старые сборки без configureDock) получают настоящую кнопку рядом с «+».
  const wfNodes = (draft.workflow as { nodes?: unknown[] } | null | undefined)?.nodes;
  const hasNodes = Array.isArray(wfNodes) ? wfNodes.length > 0 : !!graph && graph.steps.length > 0;
  const menuState = {
    t,
    hasNodes,
    deployed: !!draft.n8nWorkflowId,
    hasDraft: true,
    running: run.phase === 'starting' || run.phase === 'running',
    deleteBusy: deleting || botBusy,
    deleteDeployed: deployed,
  };
  const headItems = [
    ...(modeNow === 'schema' && nativeMethod('configureDock')
      ? [{ key: 'mode', label: t(viewOnly ? 'modeMenuEdit' : 'modeMenuView'), icon: viewOnly ? 'pencil' : 'eye' }]
      : []),
    ...scenarioMenuNativeItems(menuState),
  ];

  // Нативная шапка (приложение с мостом configureLayerHeader): рендерит null, шлёт
  // конфиг нативу и вешает html.scn-native-head (CSS прячет веб-шапку ниже). ОДНА на оба
  // состояния страницы (загрузка и готово) — тот же элемент в той же позиции дерева, значит
  // React её не перемонтирует и шапка не мигает на приходе черновика.
  const nativeHead = (
    <NativeScenarioHeader
      // Правка мастером — как создание: справа телефон-превью (владелец 2026-08-06,
      // IMG_1387) И «⋯» рядом (keepMenu, Э5 BOTS-MENU-NOTIFY): 3.21 вернула меню, значит
      // вернулась и пара кнопок. Старые сборки поле keepMenu игнорируют — у них, как
      // раньше, телефон вместо «⋯». У остальных видов кнопки-действия нет: справа «⋯».
      {...(mode === 'wizard' && wizDef && wizPhone
        ? { action: { icon: 'iphone', key: 'wiz:preview' }, keepMenu: true }
        : {})}
      title={wizardOnly ? draft.title : VIEW_META[curView].label}
      // Статус ленты второй строкой родного заголовка (правило 6, владелец 19.08): в
      // приложении веб-шапки нет, и «обновляется/обновлено» жило только в вебе. Условие
      // то же, что у веб-строки: статус есть только у схемы.
      subtitle={mode === 'schema' ? (saveStatus ?? '') : ''}
      titleIcon={VIEW_META[curView].icon}
      titleItems={titleItems}
      items={headItems}
      onAction={onHeaderAction}
    />
  );

  // INVARIANT(scriptera): экран сценария рисует СВОЮ шапку (nativeHead) и в состоянии
  // загрузки — раньше тут стоял голый скелетон без неё. Причина: слой создания въезжает
  // ДО прихода черновика (scenarioReady ждёт .cab-focus, а не данные), и без шапки натив
  // вёз пустую серую плашку вместо заголовка всю дорогу и ещё полсекунды после (видео
  // владельца 2026-08-06). Заголовок = переключатель вида — он известен из адреса и
  // устройства, данных не ждёт. Канон: docs/NATIVE-SLIDE-PLAN.md §11 п.9а и п.12.
  // Браузер: у бота нет своей страницы — под панелью живёт список «Ботов» (стек вкладок
  // оболочки), а этот компонент только открывает мастер эффектом выше.
  if (!isApp) return null;

  if (!draftless && (gone || !data)) {
    if (gone || error) {
      // «Не найден» — только настоящий 404 (в т.ч. пришедший в ответ на правку); обрыв
      // сети/5xx — честное «не удалось» с повтором (Ф28).
      return (
        <div className="rows">
          <div className="empty">
            {gone ? t('notFound') : tc('loadFailed')}{' '}
            {!gone && (
              <>
                <KButton tone="link" onClick={reload}>
                  {tc('retry')}
                </KButton>{' '}
              </>
            )}
            <Link
              href="/app/bots"
              className="lnk"
              onClick={(e) => {
                // Тоже снимаем слой родным выездом, а не обычной навигацией (иначе снимок висит).
                if (!e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey && exitScenarioLayer()) e.preventDefault();
              }}
            >
              {t('backToList')}
            </Link>
          </div>
        </div>
      );
    }
    return (
      <>
        {nativeHead}
        {/* Веб-шапка (в приложении её прячет html.scn-native-head): тот же заголовок-вид,
            что и у доехавшей страницы, — вместо скелетон-плашки. */}
        <div className="scn-bar">
          <Link href="/app/bots" className="dr-close scn-close" aria-label={t('backToList')} onClick={closeToList}>
            ✕
          </Link>
          <div className="scn-head">
            <div className="scn-view-wrap">
              <span className="scn-view-static">{VIEW_META[curView].label}</span>
            </div>
            {/* Пустая строка статуса — та же, что у доехавшей страницы (С3): без неё шапка
                подрастала бы на приходе черновика. */}
            <span className="scn-sub" />
          </div>
          <span />
        </div>
      </>
    );
  }

  // КОНСТРУКТОР ЦЕПОЧКЕ НЕ ОТКРЫВАЕТСЯ НИКОГДА (владелец 16.09.2026, М1.5: «конструктор
  // схемы я не должен видеть ни в приложении, ни в мобильном, ни в вебе, ни в каком»).
  // Адрес /app/bots/<id> у цепочки отдаёт её собственную страницу — ту же, что открывает
  // карточка в списке; правка идёт мастером поверх неё, без навигации.
  if (isChain) return <ChainPagePane draftId={draftId} />;

  return (
    <>
      {nativeHead}
      {/* Шапка iOS-стиля (A1): слева закрыть, по центру название + выпадающий выбор режима,
          справа «⋯» с действиями (Проверить, Удалить) — по разбору владельца 2026-07-06. */}
      <div className="scn-bar">
        <Link href="/app/bots" className="dr-close scn-close" aria-label={t('backToList')} onClick={closeToList}>
          ✕
        </Link>
        <div className="scn-head">
          {/* Только переключалка вида (заказ владельца 2026-07-22): название сценария,
              значок «Поделиться» и описание из веб-шапки убраны — теперь как в нативной
              шапке приложения, где между кнопками стоит один переключатель. «Поделиться»
              и «О сценарии» остались в меню «⋯». */}
          {/* Переключалка «Диалог | Холст | Лента» — «серое слово + каретка», единый
              вид с витриной (владелец 2026-07-13). */}
          <div className="scn-view-wrap">
            {wizardOnly ? (
              /* Скрипт из быстрой настройки: видов нет (владелец 05.08) — вместо
                 переключалки НАЗВАНИЕ скрипта, как в шапке при создании (владелец
                 2026-08-06: «открываю снова — должно быть один в один как при
                 создании»); слово «Настройка» там ничего не сообщало. */
              <span className="scn-view-static">{draft.title}</span>
            ) : (
              /* PopSelect — канон разовых выборов (владелец 2026-07-16): общий стиль
                  с витриной и закрытие по клику мимо. */
              <PopSelect
                align="left"
                ariaLabel={t('modeLabel')}
                label={VIEW_META[curView].label}
                /* Значки — из набора меню «⋯» (scenario-menu.tsx), фикслист 3.14: выпадашка
                   вида и меню сценария выглядят одинаково, свои SVG тут не заводим. */
                options={[
                  ...(ASSISTANT_HIDDEN
                    ? []
                    : [{ key: 'dialog', label: t('modeDialog'), icon: <ViewDialogIcon />, on: mode === 'dialog' }]),
                  {
                    key: 'canvas',
                    label: t('vertViewCanvas'),
                    icon: <ViewCanvasIcon />,
                    on: mode === 'schema' && schemaView === 'canvas',
                  },
                  {
                    key: 'lanes',
                    label: t('vertViewLanes'),
                    icon: <ViewLanesIcon />,
                    on: mode === 'schema' && schemaView === 'lanes',
                  },
                ]}
                onPick={(k) => {
                  if (k === 'dialog') setMode('dialog');
                  else {
                    setMode('schema');
                    pickView(k as 'canvas' | 'lanes');
                  }
                }}
              />
            )}
          </div>
          {/* Вторая строка шапки — статус сохранения (С3). Место под неё зарезервировано
              ВСЕГДА (пустая строка тоже занимает высоту): иначе шапка дёргалась бы на
              каждое «сохраняю…». Стоит ВПЛОТНУЮ к названию (правило 6, владелец 19.08):
              название и статус читаются одним блоком, как имя и ник в шапке переписки.
              В приложении ту же строку рисует натив — вторая строка родного заголовка
              (setLayerSubtitle, NativeScenarioHeader); в старых сборках метода нет, там
              её показывает веб отдельным фикс-слоем (.scn-sub-native). */}
          <span className="scn-sub" role="status">
            {mode === 'schema' ? (saveStatus ?? '') : ''}
          </span>
        </div>
        <div className="scn-bar-actions">
          {/* КРУЖКА «ТЕЛЕФОН» В ВЕБ-ШАПКЕ СТРАНИЦЫ БОТА НЕТ (владелец 02.09.2026, Н24:
              «справа сверху лишняя кнопка»): справа живёт только «⋯» — меню страницы, а
              предпросмотр вызывается из самого мастера. В ПРИЛОЖЕНИИ кнопка остаётся:
              её рисует натив по action iphone (nativeHead выше), и там она стоит в родной
              шапке рядом с «⋯» — веб-шапка в приложении не видна вовсе. */}
          {/* 3.21 (владелец 18.08.2026, ОТМЕНЯЕТ В1 того же дня — «это была плохая идея»):
              «⋯» вернулась на своё место справа в шапке, с прежним содержимым. Пункты,
              значки и гейты — общий scenarioMenuItems, рендер — общий ScnMenuList: этому
              меню неоткуда разойтись с карточкой списка и быстрой панелью. Переключатель
              «Просмотр ⇄ Редактирование» не выброшен — он стоит внизу, рядом с «+». */}
          <div className="scn-menu-wrap" ref={menuWrapRef}>
            <KBareButton
              className="dr-close scn-menu-btn"
              aria-label={t('menuLabel')}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={() => {
                setDelConfirm(false);
                setMenuOpen((v) => !v);
              }}
            >
              {/* Глиф «⋯» — ОДИН на весь кабинет (card-tools.tsx), своей копии не заводим. */}
              {DOTS}
            </KBareButton>
            {menuOpen ? (
              <ScnMenuList
                items={scenarioMenuItems(menuState)}
                deleteTitle={botBusy ? t('deleteBusyNote') : undefined}
                onPick={(k) => {
                  if (k === 'delete') {
                    // Меню остаётся открытым — вместо пункта раскрывается вопрос Да/Нет.
                    setDelConfirm(true);
                    return;
                  }
                  setMenuOpen(false);
                  if (k === 'about') openAbout();
                  else if (k === 'share') openShare();
                  else if (k === 'run') run.start();
                }}
                deleteConfirm={
                  delConfirm ? (
                    <div className="scn-menu-confirm" role="group">
                      <span className="confirm2-q">
                        {t(deployed ? 'deleteConfirmDeployed' : 'deleteConfirm')}
                      </span>
                      <KButton
                        tone="danger"
                        size="sm"
                        disabled={deleting || botBusy}
                        onClick={() => void removeDraft()}
                      >
                        {deleting ? t('deleteBusy') : tc('yes')}
                      </KButton>
                      <KButton
                        size="sm"
                        disabled={deleting}
                        onClick={() => setDelConfirm(false)}
                      >
                        {tc('no')}
                      </KButton>
                    </div>
                  ) : undefined
                }
              />
            ) : null}
          </div>
        </div>
      </div>

      {/* Успех — зелёный тост у нижнего края, сам гаснет; ошибки/ход — плашка сверху
          с крестиком (её надо уметь закрыть — иначе висит поверх схемы, заказ 2026-07-10). */}
      {run.note && run.note.ok ? (
        <KToast text={run.note.text} />
      ) : run.note ? (
        <div className="scn-run-note closable" role="status">
          <span className="scn-run-note-text">{run.note.text}</span>
          <KBareButton
            className="scn-run-note-x"
            aria-label={tc('close')}
            onClick={() => run.setNote(null)}
          >
            ✕
          </KBareButton>
        </div>
      ) : null}

      {/* Баннер «бот занят» (A12) снят диспетчером Ф5: много сценариев на боте — норма. */}

      {/* Режим «Настройка» (владелец 05.08): сценарий из мастера открывается той же
          анкетой, что его создала, — вместо схемы. «Сохранить» пересобирает канон
          новой ревизией (ветка wizard PUT drafts/[id]); подтверждение — зелёный тост. */}
      {mode === 'wizard' && wizDef && draft.wizard ? (
        <div className="scn-wizard-pane">
          <TemplateWizardPanel
            key={draft.id}
            def={wizDef}
            header={{
              update: () => {},
              // НЕТ РУКИ — НЕТ И КНОПКИ (К19.1 п.9): родная шапка страницы пересобирается
              // без телефона, на следующем шаге — с ним. Значение то же — React
              // перерисовку не делает.
              setPreview: (fn) => {
                wizPreviewRef.current = fn ?? (() => {});
                setWizPhone((on) => (on === (fn !== null) ? on : fn !== null));
              },
            }}
            edit={{
              draftId: draft.id,
              baseVersion: draft.version,
              initial: wizardInitial(),
              // Язык бота запечён при создании — правка его не переписывает.
              ...(draft.wizard?.locale ? { locale: draft.wizard.locale } : {}),
              ...(typeof data?.chainRunning === 'number' ? { chainRunning: data.chainRunning } : {}),
              onSaved: (d) => mutate({ ok: true, draft: d, ...(typeof data?.chainRunning === 'number' ? { chainRunning: data.chainRunning } : {}) }),
            }}
            onDone={() => {
              setWizSaved(true);
              window.setTimeout(() => setWizSaved(false), 2500);
            }}
          />
        </div>
      ) : null}
      {wizSaved ? (
        <KToast text={t('wizardSaved')} />
      ) : null}
      {/* Карточек «Нужен доступ» на холсте и в ленте НЕТ (владелец 2026-07-24: на самих
          узлах уже написано, этого достаточно); в диалоге их по-прежнему рисует чат. */}
      {!ASSISTANT_HIDDEN && mode === 'dialog' ? (
        <ScenarioChat
          draftId={draftId}
          ensureDraftId={ensureDraftId}
          chat={draft.chat}
          graph={graph}
          workflow={draft.workflow}
          nodeInfo={draft.nodeInfo}
          onDraft={(d) => mutate({ ok: true, draft: d })}
          onBusyChange={setChatBusy}
          onOpenSchema={() => setMode('schema')}
        />
      ) : null}
      {/* Схема ЖИВЁТ после первого открытия (владелец 2026-08-03 «кэшить схемы»):
          уход в «Диалог» лишь прячет её (hidden → класс scn-hide на корне панели),
          xyflow не переинициализируется — обратный щелчок мгновенный, без дёрганий.
          Все :has-правила холста в CSS выключаются на скрытой (:not(.scn-hide)). */}
      {schemaView && (mode === 'schema' || schemaSeen) ? (
        <ScenarioSchemaPanel
          draft={draft}
          ensureDraftId={ensureDraftId}
          onGone={markGone}
          onDraft={(d) => mutate({ ok: true, draft: d })}
          onReload={reload}
          view={schemaView}
          viewOnly={viewOnly}
          // 3.21: переключатель режима — кнопкой нижней панели, рядом с «+».
          onToggleViewOnly={toggleViewOnly}
          run={run}
          onAbout={openAbout}
          onStatus={setSaveStatus}
          hidden={mode !== 'schema'}
        />
      ) : null}
    </>
  );
}

// ─── Drawer «О сценарии» (DESIGN-REFRESH этап 4): название + описание ───
// Сохранение — ветка meta PUT drafts/[id]: граф и version не трогаются. На 409
// (бот/вторая вкладка успели поправить граф) повторяем один раз с живой версией —
// мета-правка поверх свежего графа безопасна, это ровно намерение юзера.
// ── Мост нативной шапки сценария (владелец 2026-07-21, по HIG) ── рендерит null.
// Пока смонтирован (сценарий открыт) и мост есть (новая сборка приложения): держит класс
// html.scn-native-head (CSS прячет веб-шапку и отступает контент), конфигурирует нативную
// шапку (заголовок + UIMenu) и слушает headerAction. Плашка сверху → шапку снять (она
// принадлежит экрану сценария); плашка ушла → вернуть. Веб/старые сборки — ничего не делает.
interface HeaderItem {
  key: string;
  label: string;
  icon?: string;
  checked?: boolean;
  destructive?: boolean;
  disabled?: boolean;
  section?: string;
}

function NativeScenarioHeader({
  title,
  subtitle,
  titleIcon,
  titleItems,
  items,
  action,
  keepMenu,
  onAction,
}: {
  title: string;
  /** Статус ленты мелкой строкой ПОД названием (правило 6, владелец 19.08): в свежих
   *  сборках её рисует натив (setLayerSubtitle), в старых — сам веб (.scn-sub-native). */
  subtitle?: string;
  /** Иконка вида слева от заголовка + родное меню видов на тапе (владелец 2026-07-21). */
  titleIcon?: string;
  titleItems?: Array<{ key: string; label: string; icon?: string; checked?: boolean }>;
  items: HeaderItem[];
  /** Вместо «⋯» — одно действие своей иконкой (правка мастером: телефон-превью). */
  action?: { icon: string; key: string };
  /** Вместе с action: «⋯» с меню ОСТАЁТСЯ, действие — отдельной кнопкой слева от него
   *  (Э5 BOTS-MENU-NOTIFY). Старые сборки поле игнорируют — действие вместо «⋯». */
  keepMenu?: boolean;
  onAction: (key: string) => void;
}) {
  const supported = nativeMethod('configureLayerHeader'); // НЕ по свойству: мост — Proxy
  // Свежие обработчик/пункты без пере-подписки: конфиг шлётся по смене СОДЕРЖИМОГО (json).
  // Ref обновляется эффектом после каждого рендера (канон файла — как saveRef).
  const actRef = useRef(onAction);
  const itemsRef = useRef(items);
  const titleItemsRef = useRef(titleItems);
  const subRef = useRef(subtitle);
  useEffect(() => {
    actRef.current = onAction;
    itemsRef.current = items;
    titleItemsRef.current = titleItems;
    subRef.current = subtitle;
  });
  const itemsJson = JSON.stringify([items, titleItems, titleIcon, action, keepMenu]);

  useEffect(() => {
    if (!supported) return;
    document.documentElement.classList.add('scn-native-head');
    let sub: { remove: () => void | Promise<void> } | null = null;
    let dead = false;
    // await, не .then: настоящий мост может вернуть хендл БЕЗ .then (капкан 2026-07-21).
    void (async () => {
      const s = await nativeBridge()?.addListener?.('headerAction', (d) => {
        if (d?.key) actRef.current(d.key);
      });
      if (!s) return;
      if (dead) void s.remove();
      else sub = s;
    })();
    return () => {
      dead = true;
      document.documentElement.classList.remove('scn-native-head');
      void nativeBridge()?.clearLayerHeader?.();
      void sub?.remove();
    };
  }, [supported]);

  // Вторую строку заголовка ставит ОТДЕЛЬНЫЙ метод (контракт уехавших заморожен, запрет
  // №8) — и спрашиваем его по имени, а не по полю: у Proxy-моста поля не нащупать.
  const subOk = nativeMethod('setLayerSubtitle');
  useEffect(() => {
    if (!supported) return;
    const apply = () => {
      const br = nativeBridge();
      if (topIsPanel()) void br?.clearLayerHeader?.();
      else {
        void br?.configureLayerHeader?.({
          title,
          items: itemsRef.current,
          ...(titleIcon ? { titleIcon } : {}),
          ...(titleItemsRef.current?.length ? { titleItems: titleItemsRef.current } : {}),
          ...(action ? { actionIcon: action.icon, actionKey: action.key } : {}),
          ...(action && keepMenu ? { keepMenu: true } : {}),
        });
        // Сразу за конфигом: шапку могли пересобрать (вернулись из-под листа) — без этого
        // вторая строка терялась бы до следующей смены статуса.
        if (subOk) void br?.setLayerSubtitle?.({ text: subRef.current ?? '' });
      }
    };
    apply();
    return subscribeTopLayer(apply);
  }, [supported, subOk, title, itemsJson]);

  // Живой статус: строка меняется куда чаще шапки, поэтому едет своим вызовом.
  useEffect(() => {
    if (!supported || !subOk) return;
    void nativeBridge()?.setLayerSubtitle?.({ text: subtitle ?? '' });
  }, [supported, subOk, subtitle]);

  // ЗАПАСНОЙ ВАРИАНТ ДЛЯ СТАРЫХ СБОРОК: метода нет — родная шапка второй строки не умеет,
  // а веб-шапку в приложении прячет html.scn-native-head. Показываем ту же строку сами,
  // фикс-слоем под родным заголовком (CSS .scn-sub-native); в вебе она не видна вовсе.
  return supported && !subOk && subtitle ? <div className="scn-sub-native">{subtitle}</div> : null;
}

/** СОХРАНИТЬ «О сценарии» — ОДНА РУКА НА ВЕБ-ФОРМУ И НА РОДНОЕ ОКНО ПРИЛОЖЕНИЯ
 *  (bot-window-native.tsx): тот же PUT, тот же набор полей и тот же повтор при
 *  разъехавшейся версии. Второго пути сохранения у карточки нет.
 *  Только картинка/название/описание/категория: настройки запуска сюда не шлём вовсе —
 *  сервер меняет лишь присланные поля, значения из шага «Старт» остаются целы. */
export async function putScenarioAbout(
  draftId: string,
  baseVersion: number,
  meta: {
    title: string;
    description: string | null;
    logo: string | null;
    category: string | null;
    /** Настройки превью карточки (П119) — поле необязательное: не прислали, не трогаем. */
    cardPrefs?: { lines: string[]; days: number; step: 'day' | 'week' } | null;
  },
): Promise<{ ok?: boolean; error?: string; version?: number; draft?: DraftSnapshot }> {
  const put = (version: number) =>
    fetch(`/api/cabinet/scenarios/drafts/${draftId}`, {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ baseVersion: version, meta }),
    })
      .then((res) => res.json() as Promise<{ ok?: boolean; error?: string; version?: number; draft?: DraftSnapshot }>)
      .catch(() => ({ ok: false }) as { ok: boolean; error?: string; version?: number; draft?: DraftSnapshot });
  const r = await put(baseVersion);
  if (r.error === 'version_conflict' && typeof r.version === 'number') return put(r.version);
  return r;
}

// export: форму звала правая панель быстрой настройки (снята 09.09.2026); остаётся ради тестов и шторки.
export function ScenarioAboutForm({
  draft,
  saveRef,
  onSaved,
}: {
  draft: DraftSnapshot;
  saveRef: React.MutableRefObject<(() => void) | null>;
  onSaved: (d: DraftSnapshot) => void;
}) {
  const t = useTranslations('cabinet.scenario');
  const drawer = useSlidePanel(); // close: в приложении снимает лист «О сценарии», в вебе — drawer
  const ta = useTranslations('cabinet.automations');
  const { favs: cats } = useFavorites('scriptCategories');
  const [title, setTitle] = useState(draft.title);
  const [desc, setDesc] = useState(draft.description ?? '');
  const [category, setCategory] = useState(draft.category ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(false);
  // ГЕРОЙ-АВАТАРКА СНЯТА С ЭТОЙ ФОРМЫ (владелец 01.09.2026, волна 3 перевода приложения
  // в натив): перебор логотипов схемы тапом и кнопка-палитра больше не показываются ни в
  // вебе, ни в приложении — эталон меняется вместе. Механика выбора в ДАННЫХ цела:
  // сохраняем ровно тот `logo`, что уже записан у черновика (картинка + суффикс цвета
  // «~cN»), форма его не трогает. Настроек ЗАПУСКА здесь тоже нет (приказ владельца
  // 30.07, В5-ОДИН-В-ОДИН): они живут в одном месте — в шаге «Старт».
  const logo = draft.logo ?? null;
  const save = async () => {
    if (busy) return;
    const nm = title.trim();
    if (!nm) {
      setErr(true);
      return;
    }
    setBusy(true);
    setErr(false);
    const r = await putScenarioAbout(draft.id, draft.version, {
      title: nm,
      description: desc.trim() || null,
      logo,
      category: category || null,
    });
    setBusy(false);
    if (r.ok && r.draft) {
      onSaved(r.draft);
      drawer.close();
      return;
    }
    setErr(true);
  };
  // ✓ в шапке drawer'а дергает свежий save (замыкание обновляется каждый рендер).
  useEffect(() => {
    saveRef.current = save;
  });

  return (
    /* Форма — на наборе пузырей (FPane, владелец 2026-08-11): те же правила вида,
       что у мастера шаблона. */
    <FPane>
      {/* Ошибка — красным сразу под шапкой (стандарт модалок, владелец 2026-07-23). */}
      {err ? <DError>{t('aboutError')}</DError> : null}
      {/* ТРИ ГРУППЫ С ПОДПИСЯМИ НАД ПУЗЫРЯМИ (владелец 13.09.2026, П80): «Название»,
          «Описание», «Категория» — один в один с родной полушторкой «О боте». Прежняя
          пара полей в одном пузыре читалась плохо, а описание терялось. */}
      <FBlock
        label={t('aboutTitleLabel')}
        {...(err && !title.trim() ? { error: t('aboutError') } : {})}
      >
        <KInput
          value={title}
          maxLength={100} /* стандарт названия — до 100 символов (владелец, 11:57) */
          disabled={busy}
          bad={err && !title.trim()}
          aria-label={t('aboutTitleLabel')}
          onChange={(e) => setTitle(e.target.value)}
        />
      </FBlock>
      <FBlock label={t('aboutDescLabel')} hint={t('aboutDescHint')}>
        {/* БОЛЬШОЕ поле (владелец 2026-07-23); авто-рост по контенту до потолка — им
            заведует сам набор (KTextarea autoGrow), своей руки над высотой тут нет. */}
        <KTextarea
          value={desc}
          autoGrow
          rows={4}
          maxLength={255} /* стандарт описания — до 255 символов (владелец, 2026-07-09) */
          disabled={busy}
          placeholder={t('aboutDescPh')}
          aria-label={t('aboutDescLabel')}
          onChange={(e) => setDesc(e.target.value)}
        />
      </FBlock>
      {/* Своя категория (владелец 2026-08-01) — ОТДЕЛЬНЫМ пузырём РАСКРЫТЫМ списком с
          галочкой (владелец 2026-08-11: выпадашка тут — брак). «Без категории» сверху;
          список ведут в колонке «Скриптов» — тут только привязка скрипта. */}
      <FChoiceBlock
        label={ta('aboutCategory')}
        value={category}
        onChange={setCategory}
        options={[
          { value: '', label: ta('aboutCategoryNone') },
          ...catList(cats).map((c) => ({ value: c.name, label: c.name })),
        ]}
      />
    </FPane>
  );
}
